import { chargeCellMs, chargeCells, chargeFullMs } from "../animations/charge";
import {
  targetFlyMs,
  targetFullMs,
  targetMarkMs,
  targetMarks,
  targetSweepStartMs,
} from "../animations/targetCharge";
import { soundAccent } from "../constants";
import {
  bell,
  readSetting,
  readUrlSetting,
  sweep,
  tone,
  withAccent,
  writeSetting,
} from "./audioEngine";
import { playSample } from "./samples";

// Welk geluid er bij welke animatie hoort. Eén plek, net als de registry:
// een animatie die hier niet in staat is stil, en dat is de meeste. Wat
// beweegt hoeft niet ook te klinken, anders is het bij elke verkoop een
// kermis.
//
// De aanloop en de aankondiging zijn het moment; de rest ligt eronder en
// blijft daarom zacht. Als je iets harder of zachter wil: de `gain` per
// geluid hieronder, of soundVolume in constants.js voor alles tegelijk.
//
// Er zijn acht sets, zodat je kunt kiezen wat bij de ruimte past. Wat een set
// niet zelf invult komt uit de basis. Kiezen doe je in het animatielab (met
// Shift en een cijfer) of met ?soundset=... achter de URL; die keuze blijft
// staan, ook na herstarten.
//
// Ligt er een eigen bestand voor een animatie (zie samples.js), dan wint dat
// van alles hieronder. Een opname is nu eenmaal voller dan wat een paar
// oscillatoren kunnen: koper met al zijn boventonen erin krijg je met één
// oscillator niet voor elkaar. Voor losse tonen is het andersom.
//
// Alles is in seconden ten opzichte van het begin van de animatie.

// De tikken van de aanloop lopen exact mee met de blokjes op het scherm: dat
// zijn dezelfde getallen, uit animations/charge.js.
const tickAt = (index) => chargeCellMs(index) / 1000;
const fullAt = chargeFullMs / 1000;

// De aanloop: een tik per blokje, steeds sneller, met een toon eronder die
// oploopt. Elke set vult zijn eigen klank in.
function ticks({ type = "square", freq = 300, rise = 900, gain = 0.09 }) {
  for (let i = 0; i < chargeCells; i += 1) {
    const part = i / (chargeCells - 1);
    tone({
      at: tickAt(i),
      freq: freq + part * part * rise,
      type,
      duration: 0.05,
      // De eerste tikken zachter dan de laatste, maar niet zo veel zachter dat
      // je het begin mist: daar hoort de aanloop juist te beginnen.
      gain: gain * (0.78 + 0.22 * part),
    });
  }
}

// ------------------------------------------------------------
// De basis: warm, met klokjes. Dit is wat je hoort als je niets kiest.
// ------------------------------------------------------------

function charge() {
  ticks({});
  tone({ at: 0, freq: 110, to: 440, type: "triangle", duration: fullAt, gain: 0.1 });
  // Het opzuigen vlak voor de flits.
  sweep({ at: fullAt - 0.3, duration: 0.32, gain: 0.1, from: 400, to: 5000, type: "highpass" });
}

// De aankondiging: eerst de klap waarmee het beeld donker wordt, dan vier
// tonen omhoog, in de maat van de regels die binnenschuiven.
function splash() {
  sweep({ at: 0, duration: 0.5, gain: 0.3, from: 1800, to: 120 });
  tone({ at: 0, freq: 90, to: 44, type: "sine", duration: 0.6, gain: 0.32 });

  const chord = [523.25, 659.25, 783.99, 1046.5];
  chord.forEach((freq, i) => bell({ at: 0.08 + i * 0.11, freq, duration: 1.2, gain: 0.2 }));
}

// De schermrand: bij een verkoop is dit het eerste wat je hoort, en het enige
// dat bij het seintje hoort. Eén lage toon die opkomt en weer wegzakt, meer
// niet - hij mag opvallen zonder dat iemand ervan schrikt.
//
// De confetti is expres stil. De kanonnen schieten bij een grote deal drie
// keer over de aankondiging, en daar nog geritsel onder is geen feest maar
// ruis.
function glow() {
  // Eerst een klokje, dan de zwelling eronder. Dat klokje is het punt: een
  // geluid dat opkomt hoor je in een ruimte met mensen niet beginnen, een
  // geluid dat ergens begint wel. Het is hetzelfde idee als de rand die
  // knippert in plaats van opkomt (zie fx-glow-pulse in animations.css).
  bell({ at: 0, freq: 880, duration: 0.7, gain: 0.16 });
  bell({ at: 0.12, freq: 1174.7, duration: 0.9, gain: 0.1 });

  tone({ at: 0, freq: 196, type: "sine", duration: 1.5, gain: 0.11, attack: 0.18 });
  tone({ at: 0.08, freq: 294, type: "sine", duration: 1.2, gain: 0.07, attack: 0.18 });
  tone({ at: 0.08, freq: 392, type: "sine", duration: 0.9, gain: 0.035, attack: 0.18 });
}

// Het record: de vlakjes op het scherm springen open, en daar horen
// fonkelingen bij - geen knallen. Dat laatste was vuurwerk van een straat
// verderop, en dat is niet wat er te zien is. De tijden lopen mee met de
// bursts in de animatielaag.
//
// De noten hieronder zijn hele verhoudingen van een grondtoon en geen ronde
// notennummers. Met zoveel tonen tegelijk gaan de boventonen anders net
// naast elkaar liggen, en dat op-en-neer hoor je als vals zodra de tv hard
// staat. Zelfde reden als in tools/geluiden.py, waar de opnames vandaan
// komen.
const fanfareRoot = 130.8125;

function fireworks() {
  // Het akkoord eronder, dat blijft staan zolang de kaart er is.
  tone({ at: 0.04, freq: fanfareRoot, type: "sine", duration: 2.8, gain: 0.12, attack: 0.04 });
  [2, 2.5, 3, 4].forEach((ratio, i) =>
    tone({
      at: 0.06 + i * 0.02,
      freq: fanfareRoot * ratio,
      type: "triangle",
      duration: 2.6,
      gain: 0.1,
      attack: 0.05,
    }),
  );

  // De fonkelingen erboven: een tikje en een hoge toon die blijft hangen,
  // per vlakje dat aanspringt.
  const glans = [8, 10, 12, 16];
  [0, 0.3, 0.6, 0.95, 1.3, 1.7, 2.1, 2.5].forEach((at, i) => {
    tone({
      at,
      freq: fanfareRoot * glans[i % glans.length],
      type: "sine",
      duration: 1.1,
      gain: 0.11 - i * 0.008,
    });
    sweep({ at, duration: 0.03, gain: 0.03, from: 7000, to: 4000, type: "bandpass", q: 3 });
  });

  // Een veeg die met het akkoord meekomt en wegzakt, zoals een bekken.
  sweep({ at: 0.04, duration: 1.8, gain: 0.07, from: 5000, to: 9000, type: "highpass" });
}

// Het eigen record: dezelfde toonsoort als het target en het record, maar
// een octaaf lager en korter. Dit komt vaker langs dan allebei, dus het is
// trots en geen feestje.
function personalBest() {
  tone({ at: 0, freq: fanfareRoot * 3, type: "triangle", duration: 0.45, gain: 0.12 });
  tone({ at: 0.16, freq: fanfareRoot * 4, type: "triangle", duration: 0.5, gain: 0.13 });

  const at = 0.34;
  tone({ at, freq: fanfareRoot, type: "sine", duration: 2, gain: 0.11, attack: 0.04 });
  [2, 2.5, 3, 4].forEach((ratio, i) =>
    tone({
      at: at + i * 0.014,
      freq: fanfareRoot * ratio,
      type: "triangle",
      duration: 1.9,
      gain: 0.1,
      attack: 0.04,
    }),
  );

  [8, 10, 12].forEach((ratio, i) =>
    tone({
      at: at + 0.1 + i * 0.22,
      freq: fanfareRoot * ratio,
      type: "sine",
      duration: 1.1,
      gain: 0.05 - i * 0.008,
    }),
  );

  sweep({ at: at - 0.04, duration: 1.4, gain: 0.05, from: 6000, to: 9000, type: "highpass" });
}

// Het teamtarget: een korte fanfare. Dit gebeurt één keer per maand, dus
// die mag groter klinken dan de rest. Zelfde toonsoort en dezelfde stemming
// als hierboven; geen bell(), want die zet zijn boventoon expres een tikje
// naast de grondtoon en dat is hier juist wat we niet willen.
function target() {
  [3, 4, 5].forEach((ratio, i) =>
    tone({
      at: i * 0.19,
      freq: fanfareRoot * ratio,
      type: "triangle",
      duration: 0.55,
      gain: 0.15,
    }),
  );

  // Het slotakkoord, dat blijft staan. De grondtoon eronder is een kale
  // sinus: die geeft het formaat zonder de onderkant vol te zetten.
  const at = 0.58;
  tone({ at, freq: fanfareRoot, type: "sine", duration: 2.4, gain: 0.13, attack: 0.03 });
  [4, 5, 6, 8].forEach((ratio, i) =>
    tone({
      at: at + i * 0.012,
      freq: fanfareRoot * ratio,
      type: "triangle",
      duration: 2.3,
      gain: 0.12,
      attack: 0.03,
    }),
  );

  [8, 10, 12].forEach((ratio, i) =>
    tone({
      at: at + 0.02 + i * 0.05,
      freq: fanfareRoot * ratio,
      type: "sine",
      duration: 1.6,
      gain: 0.05,
    }),
  );

  sweep({ at: at - 0.06, duration: 1.8, gain: 0.07, from: 5200, to: 9000, type: "highpass" });
}

// De meter naar 100: een tik op elke 5% en een toon eronder die meeloopt. De
// tikken komen uit dezelfde getallen als de wijzer (animations/
// targetCharge.js), dus ze komen steeds sneller achter elkaar, precies zoals
// hij optrekt. Op 100 een klap en een akkoord in de toonsoort van de
// fanfare, want die komt er vlak achteraan.
const targetMarkAt = (index) => targetMarkMs(index / targetMarks) / 1000;
const targetFullAt = targetFullMs / 1000;

function targetCharge() {
  // De meter stijgt op uit de hero: een zachte zwiep omhoog, en de toon die
  // mee omlaag loopt met de wijzer die leegloopt.
  const fly = targetFlyMs / 1000;
  sweep({ at: 0, duration: fly, gain: 0.08, from: 500, to: 4200, type: "bandpass", q: 2 });
  tone({ at: 0, freq: 392, to: 98, type: "triangle", duration: fly, gain: 0.06 });

  for (let i = 1; i <= targetMarks; i += 1) {
    const part = i / targetMarks;
    tone({
      at: targetMarkAt(i),
      freq: 280 + part * part * 820,
      type: "square",
      duration: 0.045,
      gain: 0.07 * (0.75 + 0.25 * part),
    });
  }

  const from = targetSweepStartMs / 1000;
  tone({
    at: from,
    freq: 98,
    to: 392,
    type: "triangle",
    duration: targetFullAt - from,
    gain: 0.1,
  });
  sweep({ at: targetFullAt - 0.35, duration: 0.36, gain: 0.1, from: 400, to: 6000, type: "highpass" });

  // De klap op 100.
  sweep({ at: targetFullAt, duration: 0.5, gain: 0.22, from: 3000, to: 120 });
  [2, 3, 4].forEach((ratio) =>
    tone({
      at: targetFullAt,
      freq: fanfareRoot * ratio,
      type: "triangle",
      duration: 0.9,
      gain: 0.1,
    }),
  );
}

// De stempel: een doffe klap op het moment dat hij neerkomt (0,2s: zo lang
// doet hij over de val, zie fx-stamp-slam), en twee tonen omhoog erachteraan.
// Kort, want de recordkaart met zijn eigen geluid komt er vlak achter.
function recordStamp() {
  const hit = 0.2;
  sweep({ at: hit, duration: 0.35, gain: 0.28, from: 1400, to: 80 });
  tone({ at: hit, freq: 110, to: 55, type: "sine", duration: 0.4, gain: 0.22 });
  tone({ at: hit + 0.12, freq: fanfareRoot * 4, type: "triangle", duration: 0.25, gain: 0.09 });
  tone({ at: hit + 0.26, freq: fanfareRoot * 6, type: "triangle", duration: 0.5, gain: 0.09 });
}

// Een stijger: twee tonen omhoog, meer niet.
function rankUp() {
  tone({ at: 0, freq: 587.33, type: "triangle", duration: 0.22, gain: 0.11 });
  tone({ at: 0.14, freq: 880, type: "triangle", duration: 0.34, gain: 0.11 });
}

// De kaart "Verkopen gemist" komt bij het aanzetten van de tv. Rustig dus:
// twee klokjes en verder niets, want hier is niemand op aan het wachten.
function missed() {
  bell({ at: 0, freq: 523.25, duration: 1.4, gain: 0.12 });
  bell({ at: 0.24, freq: 783.99, duration: 1.8, gain: 0.1 });
}

const base = {
  "sale-charge": charge,
  "sale-splash": splash,
  "screen-glow": glow,
  fireworks,
  "personal-best": personalBest,
  "target-charge": targetCharge,
  "target-hit": target,
  "rank-up": rankUp,
  "record-stamp": recordStamp,
  "missed-sales": missed,
};

// ------------------------------------------------------------
// Arcade: blokgolven, als een spelcomputer. De tikken lopen een toonladder
// op in plaats van vloeiend omhoog, en de aankondiging eindigt op het
// muntgeluid dat iedereen kent.
// ------------------------------------------------------------

// Een toonladder die per tik een trede opschuift. Los van de tikken
// hierboven, want die glijden en deze stapt.
const arcadeScale = [392, 440, 494, 523.25, 587.33, 659.25, 740, 784];

function arcadeCharge() {
  for (let i = 0; i < chargeCells; i += 1) {
    const part = i / (chargeCells - 1);
    const note = arcadeScale[Math.min(arcadeScale.length - 1, Math.floor(part * arcadeScale.length))];
    tone({ at: tickAt(i), freq: note * 2, type: "square", duration: 0.06, gain: 0.05 + part * 0.03 });
    tone({ at: tickAt(i), freq: note / 2, type: "square", duration: 0.09, gain: 0.03 });
  }
}

function arcadeSplash() {
  // De klap eerst, dan het muntje: twee tonen, de tweede blijft hangen.
  sweep({ at: 0, duration: 0.3, gain: 0.22, from: 1600, to: 200 });
  tone({ at: 0.06, freq: 987.77, type: "square", duration: 0.09, gain: 0.16 });
  tone({ at: 0.15, freq: 1318.5, type: "square", duration: 0.55, gain: 0.16 });
  // En een akkoord eronder, zodat het niet te dun wordt op een tv.
  [329.63, 493.88].forEach((freq) =>
    tone({ at: 0.15, freq, type: "triangle", duration: 0.9, gain: 0.07 }),
  );
}

function arcadeRankUp() {
  [523.25, 659.25, 880].forEach((freq, i) =>
    tone({ at: i * 0.07, freq, type: "square", duration: 0.12, gain: 0.09 }),
  );
}

// ------------------------------------------------------------
// Zacht: laag en rustig, voor een ruimte waar mensen aan het bellen zijn.
// Geen klappen, geen blokgolven, alles een stuk stiller.
// ------------------------------------------------------------

function softCharge() {
  // Een sinus draagt minder ver dan een blokgolf, dus die mag hoger staan om
  // even goed hoorbaar te zijn. Met een tweede toon erbij: één sinus alleen
  // klinkt in een ruimte al gauw als niets.
  ticks({ type: "sine", freq: 440, rise: 500, gain: 0.085 });
  ticks({ type: "triangle", freq: 880, rise: 1000, gain: 0.03 });
  tone({ at: 0, freq: 147, to: 294, type: "sine", duration: fullAt, gain: 0.09 });
}

function softSplash() {
  // Geen klap eronder: alleen een akkoord dat opkomt en blijft hangen.
  [392, 493.88, 587.33, 783.99].forEach((freq, i) =>
    bell({ at: i * 0.14, freq, duration: 1.8, gain: 0.12 }),
  );
}

// ------------------------------------------------------------
// Kristal: hoog en helder, alsof er glas wordt aangeslagen. Geen klap
// eronder; dit is de set die het minste ruimte inneemt en toch opvalt.
// ------------------------------------------------------------

const crystalScale = [784, 880, 1046.5, 1174.7, 1318.5, 1568, 1760, 2093];

function crystalCharge() {
  for (let i = 0; i < chargeCells; i += 1) {
    const part = i / (chargeCells - 1);
    const note = crystalScale[Math.min(crystalScale.length - 1, Math.floor(part * crystalScale.length))];
    tone({ at: tickAt(i), freq: note, type: "sine", duration: 0.12, gain: 0.03 + part * 0.03 });
    tone({ at: tickAt(i), freq: note * 3.01, type: "sine", duration: 0.05, gain: 0.012 });
  }
}

function crystalSplash() {
  sweep({ at: 0, duration: 0.5, gain: 0.07, from: 2000, to: 9000, type: "highpass" });
  [1046.5, 1318.5, 1568, 2093].forEach((freq, i) => {
    tone({ at: i * 0.09, freq, type: "sine", duration: 1.6, gain: 0.1 });
    tone({ at: i * 0.09, freq: freq * 2.4, type: "sine", duration: 0.7, gain: 0.04 });
  });
  // Twee tonen vlak naast elkaar gaan uit de pas lopen: dat is het glinsteren.
  tone({ at: 0.1, freq: 3136, type: "sine", duration: 2, gain: 0.02 });
  tone({ at: 0.1, freq: 3139.5, type: "sine", duration: 2, gain: 0.02 });
}

// ------------------------------------------------------------
// Diep: bioscoop. Laag, breed, weinig hoog. Werkt alleen als de tv er een
// beetje low-end uit krijgt; op een dun luidsprekertje hoor je bijna niets.
// ------------------------------------------------------------

function deepCharge() {
  ticks({ type: "sine", freq: 80, rise: 170, gain: 0.12 });
  // Een lage toon die de hele aanloop lang opkomt: dat is de spanning.
  tone({ at: 0, freq: 45, to: 90, type: "sine", duration: fullAt + 0.3, gain: 0.16 });
  sweep({ at: fullAt - 0.7, duration: 0.7, gain: 0.07, from: 200, to: 1800, type: "lowpass" });
}

function deepSplash() {
  // De dreun: laag, lang, en er hoeft verder niets bij.
  tone({ at: 0, freq: 110, to: 34, type: "sine", duration: 1.4, gain: 0.34 });
  sweep({ at: 0, duration: 0.7, gain: 0.16, from: 900, to: 70 });
  [98, 146.83, 196].forEach((freq) =>
    tone({ at: 0.06, freq, type: "triangle", duration: 1.8, gain: 0.09, attack: 0.04 }),
  );
}

// ------------------------------------------------------------
// Kassa: geld. Een mechanische aanloop en daarna de twee tikken van een
// kassalade, want dat is het geluid dat iedereen meteen thuisbrengt.
// ------------------------------------------------------------

function tillCharge() {
  for (let i = 0; i < chargeCells; i += 1) {
    const part = i / (chargeCells - 1);
    // Een klik van metaal: kort, smal, en verder niets.
    sweep({
      at: tickAt(i),
      duration: 0.03,
      gain: 0.05 + part * 0.03,
      from: 1800 + part * 1200,
      to: 900,
      type: "bandpass",
      q: 5,
    });
  }
  tone({ at: 0, freq: 90, to: 260, type: "triangle", duration: fullAt, gain: 0.06 });
}

function tillSplash() {
  // Ker-ching: de lade die opengaat en twee bellen erbovenop.
  sweep({ at: 0, duration: 0.09, gain: 0.16, from: 2600, to: 900, type: "bandpass", q: 3 });
  tone({ at: 0, freq: 120, to: 60, type: "sine", duration: 0.35, gain: 0.16 });
  bell({ at: 0.04, freq: 1318.5, duration: 0.9, gain: 0.2 });
  bell({ at: 0.14, freq: 1760, duration: 1.4, gain: 0.18 });
  [523.25, 659.25].forEach((freq) =>
    tone({ at: 0.14, freq, type: "triangle", duration: 1.1, gain: 0.07 }),
  );
}

// ------------------------------------------------------------
// Chip: helemaal 8-bit, inclusief de ruis die vroeger voor een drum
// doorging. Luidruchtig; dit is de set voor een vrijdagmiddag.
// ------------------------------------------------------------

function chipCharge() {
  const run = [262, 294, 330, 349, 392, 440, 494, 523];
  for (let i = 0; i < chargeCells; i += 1) {
    const part = i / (chargeCells - 1);
    const note = run[i % run.length] * (i < chargeCells / 2 ? 1 : 2);
    tone({ at: tickAt(i), freq: note, type: "square", duration: 0.05, gain: 0.05 + part * 0.03 });
    // Om de twee een ruistik, als een hihat.
    if (i % 2 === 1) {
      sweep({ at: tickAt(i), duration: 0.025, gain: 0.03, from: 8000, to: 4000, type: "highpass" });
    }
  }
}

function chipSplash() {
  // Het rijtje omhoog dat elke spelcomputer speelt als je iets gehaald hebt.
  [523.25, 659.25, 783.99, 1046.5, 1318.5].forEach((freq, i) =>
    tone({ at: i * 0.06, freq, type: "square", duration: 0.09, gain: 0.11 }),
  );
  tone({ at: 0.3, freq: 1046.5, type: "square", duration: 0.7, gain: 0.12 });
  tone({ at: 0.3, freq: 1568, type: "square", duration: 0.7, gain: 0.07 });
  // En de ruis eronder, de drum.
  [0, 0.12, 0.24, 0.3].forEach((at) =>
    sweep({ at, duration: 0.07, gain: 0.07, from: 6000, to: 1200, type: "highpass" }),
  );
}

// ------------------------------------------------------------
// Fluister: bijna stil. Voor als er gebeld wordt in dezelfde ruimte. Je hoort
// dat er iets gebeurt en verder niets; kijken moet je dan zelf.
// ------------------------------------------------------------

function whisperCharge() {
  // Alleen de laatste tikken, en zacht: de aanloop is dan een seintje in
  // plaats van een aftelklok.
  for (let i = chargeCells - 6; i < chargeCells; i += 1) {
    tone({ at: tickAt(i), freq: 660, type: "sine", duration: 0.07, gain: 0.018 });
  }
}

function whisperSplash() {
  bell({ at: 0, freq: 783.99, duration: 1.6, gain: 0.05 });
  bell({ at: 0.18, freq: 1046.5, duration: 2, gain: 0.04 });
}

// De meter naar 100 zonder aftelklok: alleen de laatste paar tikken en een
// klokje op 100.
function whisperTargetCharge() {
  for (let i = targetMarks - 4; i <= targetMarks; i += 1) {
    tone({ at: targetMarkAt(i), freq: 660, type: "sine", duration: 0.07, gain: 0.018 });
  }
  bell({ at: targetFullAt, freq: 1046.5, duration: 1.4, gain: 0.05 });
}

// Het seintje komt bij iedereen boven de rest uit (zie accented), en dat is
// precies wat deze set niet wil. Dus hier alleen een klokje.
function whisperGlow() {
  bell({ at: 0, freq: 880, duration: 0.6, gain: 0.045 });
}

const sets = {
  klok: {
    name: "Klok",
    note: "warm, met klokjes",
    cues: {},
  },
  arcade: {
    name: "Arcade",
    note: "blokgolven, als een spelcomputer",
    cues: {
      "sale-charge": arcadeCharge,
      "sale-splash": arcadeSplash,
      "rank-up": arcadeRankUp,
    },
  },
  zacht: {
    name: "Zacht",
    note: "laag en rustig, voor een stille ruimte",
    cues: {
      "sale-charge": softCharge,
      "sale-splash": softSplash,
    },
  },
  kristal: {
    name: "Kristal",
    note: "hoog en helder, als glas",
    cues: {
      "sale-charge": crystalCharge,
      "sale-splash": crystalSplash,
    },
  },
  diep: {
    name: "Diep",
    note: "bioscoop: laag en breed, weinig hoog",
    cues: {
      "sale-charge": deepCharge,
      "sale-splash": deepSplash,
    },
  },
  kassa: {
    name: "Kassa",
    note: "ker-ching, de lade van een kassa",
    cues: {
      "sale-charge": tillCharge,
      "sale-splash": tillSplash,
    },
  },
  chip: {
    name: "Chip",
    note: "helemaal 8-bit, voor een vrijdagmiddag",
    cues: {
      "sale-charge": chipCharge,
      "sale-splash": chipSplash,
      "rank-up": arcadeRankUp,
    },
  },
  fluister: {
    name: "Fluister",
    note: "bijna stil, voor als er gebeld wordt",
    // De vaste geluiden kan deze set niet uitzetten, maar wel veel zachter
    // zetten: dat is precies waar sampleGain voor is.
    sampleGain: 0.3,
    cues: {
      "screen-glow": whisperGlow,
      "sale-charge": whisperCharge,
      "sale-splash": whisperSplash,
      "target-charge": whisperTargetCharge,
    },
  },
};

// Voor het paneel in het lab: de sets in de volgorde waarin ze daar staan.
export const soundSets = Object.entries(sets).map(([id, set]) => ({
  id,
  name: set.name,
  note: set.note,
}));

const storeKey = "tdSoundSet";
const ids = soundSets.map((set) => set.id);

// Waar hij op staat als er niets gekozen is. "Zacht" is de rustigste: geen
// klappen, geen blokgolven, en het minste geknetter onder de aankondiging.
const fallback = "zacht";

let chosen = null;

export function soundSet() {
  if (chosen) return chosen;
  const asked = readUrlSetting("soundset", ids, storeKey);
  const saved = asked || readSetting(storeKey);
  chosen = ids.includes(saved) ? saved : fallback;
  return chosen;
}

export function setSoundSet(id, { remember = true } = {}) {
  if (!ids.includes(id)) return;
  chosen = id;
  if (remember) writeSetting(storeKey, id);
}

// Wat er vast staat: hier kiest de set niets, maar kies jij het bestand. Dit
// is wat er bij een verkoop valt, en het is er in smaken; zie sampleOptions
// in samples.js en het blok "Vaste geluiden" in het animatielab.
const fixed = ["fireworks", "personal-best"];

// En wat er bovenuit mag komen: het seintje, de aanloop en de aankondiging,
// en de meter naar 100 bij het target. Dat zijn de momenten; de balk en de rest liggen
// eronder. Hoeveel harder staat in constants.js (soundAccent).
const accented = ["screen-glow", "sale-charge", "sale-splash", "target-charge"];

const accentFor = (id) => (accented.includes(id) ? soundAccent : 1);

// Eén geluid uit één set, in deze volgorde:
//
//   1. een vast geluid hierboven, als het daarover gaat
//   2. anders wat de set zelf voor deze animatie heeft
//   3. anders het bestand uit public/sounds/, als dat er ligt
//   4. anders wat het dashboard er zelf van maakt
//
// Kort gezegd: de sets gaan over de aanloop en de aankondiging, de vaste
// geluiden over wat daarna valt. Wil je de opname van de aankondiging horen,
// kies dan een set die daar zelf niets voor heeft (Klok).
function fire(id, setId) {
  const set = sets[setId] || sets[fallback];
  const loud = (set.sampleGain ?? 1) * accentFor(id);

  // Wat vast staat: daar kies je één keer een bestand voor en dat hoor je in
  // elke set. Een set mag er wel zachter aan draaien (sampleGain), maar niet
  // iets anders van maken.
  if (fixed.includes(id) && playSample(id, loud)) return;

  const cue = set.cues[id];
  if (cue) {
    withAccent(accentFor(id), cue);
    return;
  }

  if (playSample(id, loud)) return;
  if (base[id]) withAccent(accentFor(id), base[id]);
}

// Het geluid bij een animatie, als hij er een heeft. De engine kijkt zelf of
// het geluid aan mag staan, dus dit mag altijd aangeroepen worden.
export function playCue(id) {
  fire(id, soundSet());
}

// Een set voorproeven zonder er iets bij te zien: de aanloop en daarna de
// aankondiging, precies zo lang als op de tv. Daar verschillen de sets in,
// dus dit is wat je wil horen voor je kiest.
export function previewSoundSet(id) {
  // Via dezelfde weg als op de tv, dus inclusief een eigen bestand als dat er
  // ligt: je hoort wat je krijgt, niet wat er in de code staat.
  fire("sale-charge", id);
  setTimeout(() => fire("sale-splash", id), chargeFullMs);
}
